// src/pages/Home.jsx
import React, { useEffect, useRef, useState } from "react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import MicIcon from "@mui/icons-material/Mic";
import CampaignIcon from "@mui/icons-material/Campaign";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import HandshakeIcon from "@mui/icons-material/Handshake";
import EventIcon from "@mui/icons-material/Event";
import GavelIcon from "@mui/icons-material/Gavel";
import emailjs from '@emailjs/browser';
import Momentsia_logo from "../images/Momentsia _logo.png";

const T1 = "#0ecfb0";
const T2 = "#0aa896";
const T3 = "#077a6e";
const NAVY = "#0d1b2a";
const DARK = "#1a1a1a";
const TEAL_GRAD = `linear-gradient(135deg, ${T1}, ${T3})`;

// EmailJS Configuration
const EMAILJS_SERVICE_ID = "service_zieecow";
const EMAILJS_TEMPLATE_ID = "template_l1xmaal";
const EMAILJS_PUBLIC_KEY = "q2O06yijgnOJTch1m";

const eventTypes = [
  "Corporate Event",
  "Wedding",
  "Birthday Party",
  "Conference",
  "Product Launch",
  "Music Concert",
  "Other",
];

// Logo Component
const MomentsiaLogo = ({ light = false }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
    <img src={Momentsia_logo} alt="Momentsia" style={{ height: 48, width: "auto", objectFit: "contain" }} />
  </div>
);

// ---------- CONSULT DIALOG ----------
const ConsultDialog = ({ open, onClose }) => {
  const [dialogFormData, setDialogFormData] = useState({
    name: "",
    phone: "",
    email: "",
    eventType: "",
    date: "",
    guests: "",
    message: ""
  });
  const [dialogErrors, setDialogErrors] = useState({});
  const [dialogStatus, setDialogStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateDialogForm = () => {
    const newErrors = {};
    if (!dialogFormData.name?.trim()) newErrors.name = "Name is required";
    if (!dialogFormData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(dialogFormData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!dialogFormData.phone?.trim()) newErrors.phone = "Phone number is required";
    if (!dialogFormData.eventType) newErrors.eventType = "Event type is required";
    if (!dialogFormData.date) newErrors.date = "Date is required";
    setDialogErrors(newErrors);
    return newErrors;
  };

  const handleDialogChange = (e) => {
    const { name, value } = e.target;
    setDialogFormData({ ...dialogFormData, [name]: value });
    if (dialogErrors[name]) {
      setDialogErrors({ ...dialogErrors, [name]: "" });
    }
  };

  const handleDialogSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateDialogForm();
    if (Object.keys(validationErrors).length > 0) return;

    setIsSubmitting(true);
    setDialogStatus("Sending...");

    const templateParams = {
      from_name: dialogFormData.name.trim(),
      from_email: dialogFormData.email.trim(),
      from_phone: dialogFormData.phone?.trim() || "Not provided",
      event_type: dialogFormData.eventType,
      event_date: dialogFormData.date,
      guests: dialogFormData.guests?.toString().trim() || "Not specified",
      message: dialogFormData.message?.trim() || "No additional message provided",
      submitted_at: new Date().toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    try {
      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      console.log("Dialog - Email sent successfully!", response);
      setDialogStatus("✓ Request sent successfully! We'll contact you within 24 hours.");
      setDialogFormData({ name: "", phone: "", email: "", eventType: "", date: "", guests: "", message: "" });
      setDialogErrors({});

      setTimeout(() => {
        setDialogStatus("");
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Dialog - Failed to send email:", error);
      setDialogStatus("❌ Failed to send. Please try again or call us directly.");
      setTimeout(() => setDialogStatus(""), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)" }} />
      <div style={{ position: "relative", background: "#fff", borderRadius: 14, padding: "40px 36px", maxWidth: 520, width: "90%", boxShadow: "0 24px 64px rgba(0,0,0,0.25)", maxHeight: "90vh", overflowY: "auto" }}>
        <button onClick={onClose} style={{ position: "absolute", top: 14, right: 18, background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#aaa" }}>✕</button>
        <div style={{ height: 4, background: TEAL_GRAD, borderRadius: 4, marginBottom: 24 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <img src={Momentsia_logo} alt="Momentsia" style={{ height: 32, width: "auto" }} />
          <h3 style={{ margin: 0, color: DARK, fontWeight: 800, fontSize: 20 }}>Plan Your Perfect Event</h3>
        </div>
        <p style={{ color: "#888", fontSize: 13, margin: "0 0 22px" }}>Share your event details – we'll create a customized proposal within 24 hours.</p>

        <form onSubmit={handleDialogSubmit}>
          <input
            type="text"
            name="name"
            placeholder="Full Name *"
            value={dialogFormData.name}
            onChange={handleDialogChange}
            style={{ display: "block", width: "100%", marginBottom: 13, padding: "11px 14px", border: `1.5px solid ${dialogErrors.name ? "#f44336" : "#e0e0e0"}`, borderRadius: 7, fontSize: 14, boxSizing: "border-box" }}
          />
          {dialogErrors.name && <p style={{ color: "#f44336", fontSize: 11, margin: "-8px 0 12px 0" }}>{dialogErrors.name}</p>}

          <input
            type="tel"
            name="phone"
            placeholder="Phone Number *"
            value={dialogFormData.phone}
            onChange={handleDialogChange}
            style={{ display: "block", width: "100%", marginBottom: 13, padding: "11px 14px", border: `1.5px solid ${dialogErrors.phone ? "#f44336" : "#e0e0e0"}`, borderRadius: 7, fontSize: 14, boxSizing: "border-box" }}
          />
          {dialogErrors.phone && <p style={{ color: "#f44336", fontSize: 11, margin: "-8px 0 12px 0" }}>{dialogErrors.phone}</p>}

          <input
            type="email"
            name="email"
            placeholder="Email Address *"
            value={dialogFormData.email}
            onChange={handleDialogChange}
            style={{ display: "block", width: "100%", marginBottom: 13, padding: "11px 14px", border: `1.5px solid ${dialogErrors.email ? "#f44336" : "#e0e0e0"}`, borderRadius: 7, fontSize: 14, boxSizing: "border-box" }}
          />
          {dialogErrors.email && <p style={{ color: "#f44336", fontSize: 11, margin: "-8px 0 12px 0" }}>{dialogErrors.email}</p>}

          <select
            name="eventType"
            value={dialogFormData.eventType}
            onChange={handleDialogChange}
            required
            style={{ display: "block", width: "100%", marginBottom: 13, padding: "11px 14px", border: `1.5px solid ${dialogErrors.eventType ? "#f44336" : "#e0e0e0"}`, borderRadius: 7, fontSize: 14, background: "#fff" }}
          >
            <option value="">Select Event Type *</option>
            {eventTypes.map(type => <option key={type} value={type}>{type}</option>)}
          </select>
          {dialogErrors.eventType && <p style={{ color: "#f44336", fontSize: 11, margin: "-8px 0 12px 0" }}>{dialogErrors.eventType}</p>}

          <input
            type="date"
            name="date"
            value={dialogFormData.date}
            onChange={handleDialogChange}
            required
            style={{ display: "block", width: "100%", marginBottom: 13, padding: "11px 14px", border: `1.5px solid ${dialogErrors.date ? "#f44336" : "#e0e0e0"}`, borderRadius: 7, fontSize: 14 }}
          />
          {dialogErrors.date && <p style={{ color: "#f44336", fontSize: 11, margin: "-8px 0 12px 0" }}>{dialogErrors.date}</p>}

          <input
            type="number"
            name="guests"
            placeholder="Approximate Guests"
            value={dialogFormData.guests}
            onChange={handleDialogChange}
            style={{ display: "block", width: "100%", marginBottom: 13, padding: "11px 14px", border: "1.5px solid #e0e0e0", borderRadius: 7, fontSize: 14 }}
          />

          <textarea
            name="message"
            placeholder="Tell us about your event (themes, requirements, ideas)..."
            rows={3}
            value={dialogFormData.message}
            onChange={handleDialogChange}
            style={{ display: "block", width: "100%", marginBottom: 18, padding: "11px 14px", border: "1.5px solid #e0e0e0", borderRadius: 7, fontSize: 14, resize: "vertical" }}
          />

          <button
            type="submit"
            disabled={isSubmitting}
            style={{ width: "100%", padding: "13px 0", background: TEAL_GRAD, color: "#fff", border: "none", borderRadius: 7, fontWeight: 700, fontSize: 15, cursor: isSubmitting ? "not-allowed" : "pointer", opacity: isSubmitting ? 0.7 : 1 }}
          >
            {isSubmitting ? "Submitting..." : "Submit Inquiry"}
          </button>
          {dialogStatus && <p style={{ marginTop: 16, fontSize: 12, textAlign: "center", color: dialogStatus.includes("✓") ? T2 : T1 }}>{dialogStatus}</p>}
        </form>
      </div>
    </div>
  );
};

// ---------- DATA ----------
const heroSlides = [
  { img: "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=1920&q=80", caption: "An Event Is Not a Setup — It's a Feeling" },
  { img: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1600&h=900&fit=crop", caption: "We design celebrations around people, not decorations" },
  { img: "https://images.unsplash.com/photo-1469371670807-013ccf25f16a?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8ZXZlbnQlMjBtYW5hZ2VtZW50fGVufDB8fDB8fHww", caption: "Celebrate Without Stress — We manage the details" },
  { img: "https://images.pexels.com/photos/587741/pexels-photo-587741.jpeg", caption: "Creating experiences that stay long after the lights fade" },
  {
    img: "https://images.unsplash.com/photo-1711301633355-d10a31380c44?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    caption: "Moments Are Under Capturing , Get in Touch with Us for Forever Moments."
  }
];

const ourServiceCards = [
  { img: "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&h=600&fit=crop", title: "Event Planning & Coordination" },
  { img: "https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=800&h=600&fit=crop", title: "Creative Design & Theming" },
  { img: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=800&h=600&fit=crop", title: "Vendor Management" },
];

const serviceGrid = [
  { img: "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&h=600&fit=crop", title: "Event Planning & Coordination" },
  { img: "https://images.unsplash.com/photo-1519741497674-611481863552?w=800&h=600&fit=crop", title: "Creative Design & Theming" },
  { img: "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=800&h=600&fit=crop", title: "Vendor Management" },
  { img: "https://images.unsplash.com/photo-1555244162-803834f70033?w=800&h=600&fit=crop", title: "Catering & Hospitality" },
  { img: "https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=800&h=600&fit=crop", title: "Entertainment Booking" },
  { img: "https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=800&h=600&fit=crop", title: "Photography & Videography" },
];

const talentServices = [
  {
    icon: <PeopleAltIcon sx={{ fontSize: 35 }} />,
    title: "Talent Management",
    description: "360° career development with personalized strategies for long-term success in entertainment industry.",
    premium: true,
    stats: "Best Artists",
    img: "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=600&h=400&fit=crop",
  },
  {
    icon: <MicIcon sx={{ fontSize: 35 }} />,
    title: "Artist Booking & Representation",
    description: "Exclusive booking network with venues and premium event access across India & International.",
    premium: true,
    stats: "Events",
    img: "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=600&h=400&fit=crop",
  },
  {
    icon: <CampaignIcon sx={{ fontSize: 35 }} />,
    title: "PR & Media Management",
    description: "Strategic media presence across 50+ national & regional publications with crisis management.",
    stats: "Features",
    img: "https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=600&h=400&fit=crop",
  },
  {
    icon: <TrendingUpIcon sx={{ fontSize: 35 }} />,
    title: "Digital Growth & Personal Branding",
    description: "Data-driven social media growth, content strategy, and viral marketing campaigns.",
    stats: "High Reach",
    img: "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=600&h=400&fit=crop",
  },
  {
    icon: <HandshakeIcon sx={{ fontSize: 35 }} />,
    title: "Brand Collaboration & Endorsement",
    description: "Premium brand partnerships with top labels and endorsement deal negotiations.",
    premium: true,
    stats: "Brands",
    img: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop",
  },
  {
    icon: <EventIcon sx={{ fontSize: 35 }} />,
    title: "Event & Performance Management",
    description: "End-to-end production, stage management, and technical direction for flawless performances.",
    stats: "Shows",
    img: "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=600&h=400&fit=crop",
  },
  {
    icon: <GavelIcon sx={{ fontSize: 35 }} />,
    title: "Legal & Contract Support",
    description: "Expert legal counsel, royalty collection, IP protection, and contract negotiation.",
    premium: true,
    stats: "Contracts",
    img: "https://images.unsplash.com/photo-1589578228447-e1a4e481c6c8?w=600&h=400&fit=crop",
  },
];

const talentTypes = [
  { name: "Singers", icon: "🎤", color: "#FF6B6B" },
  { name: "Bands", icon: "🎸", color: "#4ECDC4" },
  { name: "DJs", icon: "🎧", color: "#9B59B6" },
  { name: "Drummers", icon: "🥁", color: "#E67E22" },
  { name: "Influencers", icon: "📱", color: "#3498DB" },
  { name: "Anchors", icon: "🎙️", color: "#F1C40F" },
  { name: "Performers", icon: "✨", color: "#E84342" },
];

const eventCategories = [
  { name: "Corporate & Brand Events", img: "https://images.unsplash.com/photo-1527529482837-4698179dc6ce?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" },
  { name: "Weddings & Multi-Day Celebrations", img: "https://images.unsplash.com/photo-1519741497674-611481863552?w=600&h=400&fit=crop" },
  { name: "Cultural & Traditional Functions", img: "https://images.unsplash.com/photo-1711301633355-d10a31380c44?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" },
  { name: "Luxury & Destination Events", img: "https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=600&h=400&fit=crop" },
  { name: "Product Launches", img: "https://images.unsplash.com/photo-1556761175-4b46a572b786?w=600&h=400&fit=crop" },
  { name: "Music Concerts", img: "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=600&h=400&fit=crop" },
];

const testimonials = [
  { name: "Amit Basu", designation: "Marketing Director • TechCorp", text: "The team transformed our annual conference into an unforgettable experience. Professional, creative, and efficient!", stars: 5 },
  { name: "Somnath Chowdhury", designation: "CEO • EALP", text: "Our product launch exceeded all expectations. The attention to detail was remarkable.", stars: 5 },
  { name: "Sudipta Das", designation: "Bride", text: "Our wedding was absolutely perfect. They handled everything seamlessly, allowing us to enjoy our special day.", stars: 5 },
  { name: "Rita Roy", designation: "HR Manager • Global Inc", text: "The team-building event organized by Elevate Events boosted employee morale significantly.", stars: 5 },
  { name: "Suman Kumar Das", designation: "Artist", text: "From independent artist to national sensation - their talent management ecosystem transformed my career completely.", stars: 5 },
];

// ════════════════════ MAIN COMPONENT ════════════════════
export default function Home() {
  const [slideIdx, setSlideIdx] = useState(0);
  const [openForm, setOpenForm] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setInterval(() => setSlideIdx(i => (i + 1) % heroSlides.length), 4500);
    return () => clearInterval(timerRef.current);
  }, []);

  const prev = () => { 
    clearInterval(timerRef.current); 
    setSlideIdx(i => (i - 1 + heroSlides.length) % heroSlides.length); 
  };
  
  const next = () => { 
    clearInterval(timerRef.current); 
    setSlideIdx(i => (i + 1) % heroSlides.length); 
  };

  return (
    <div style={{ fontFamily: "'Segoe UI', Arial, sans-serif", margin: 0, padding: 0, color: DARK }}>
      <link href="https://fonts.googleapis.com/css2?family=Allura&display=swap" rel="stylesheet" />
      
      <style>{`
        *{box-sizing:border-box;}body{margin:0;}
        h1, h2 { font-family: 'Allura', cursive !important; letter-spacing: 1px; }
        h3, h4, h5, h6, p, a, li, button, input, textarea, .description, .stats, .testimonial-text {
          font-family: 'Segoe UI', Arial, sans-serif;
        }
        .sc:hover{transform:translateY(-6px);box-shadow:0 20px 40px rgba(14,207,176,0.25)!important;}
        .sc:hover img{transform:scale(1.05);}
        .os-c:hover img{transform:scale(1.07);}
        .os-c:hover .ov{background:linear-gradient(transparent,rgba(7,122,110,0.88))!important;}
        .ts-card, .ec-card, .tt-card{transition:transform 0.3s, box-shadow 0.3s;}
        .ts-card:hover, .ec-card:hover, .tt-card:hover{transform:translateY(-6px);box-shadow:0 20px 30px rgba(14,207,176,0.2)!important;}
        .tc:hover{box-shadow:0 12px 32px rgba(14,207,176,0.16)!important;border-color:${T1}!important;}
        .cb:hover{opacity:0.88;transform:translateY(-1px);}
        @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        .hcap{animation:fadeUp 0.7s ease both;}
      `}</style>

      {/* Navigation Component */}
      <Navigation onPlanEventClick={() => setOpenForm(true)} />

      {/* HERO SECTION */}
      <div style={{ position: "relative", width: "100%", background: "#000" }}>
        <div style={{ position: "relative", width: "100%", height: "calc(100vh - 80px)", minHeight: 520, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <img key={slideIdx} src={heroSlides[slideIdx].img} alt={heroSlides[slideIdx].caption}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 1 }} />
          <div style={{ position: "absolute", inset: 0, background: `linear-gradient(155deg,rgba(7,122,110,0.58) 0%,rgba(0,0,0,0.3) 55%,rgba(0,0,0,0.52) 100%)`, zIndex: 2 }} />
          <div className="hcap" style={{ position: "relative", zIndex: 3, textAlign: "center", width: "90%", maxWidth: 880, margin: "0 auto" }}>
            <div style={{ width: 60, height: 3.5, background: TEAL_GRAD, borderRadius: 2, margin: "0 auto 18px" }} />
            <h1 style={{ color: "#fff", fontSize: "clamp(1.6rem,4.5vw,3rem)", fontWeight: 800, lineHeight: 1.22, textShadow: "0 2px 20px rgba(0,0,0,0.5)", margin: "0 0 14px" }}>
              {heroSlides[slideIdx].caption}
            </h1>
            <p style={{ color: "rgba(255,255,255,0.88)", fontSize: "clamp(0.9rem,2vw,1.1rem)", margin: "0 0 28px" }}>
              We manage the details — you enjoy the moments.
            </p>
            <button className="cb" onClick={() => setOpenForm(true)} style={{ background: TEAL_GRAD, color: "#fff", border: "none", borderRadius: 8, padding: "14px 40px", fontWeight: 700, fontSize: 15, cursor: "pointer", boxShadow: `0 8px 26px rgba(14,207,176,0.45)` }}>
              Plan Your Event
            </button>
          </div>
          <button onClick={prev} style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", zIndex: 5, background: "rgba(14,207,176,0.18)", border: `1.5px solid rgba(14,207,176,0.4)`, color: "#fff", width: 46, height: 46, borderRadius: "50%", fontSize: 24, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(6px)" }}>‹</button>
          <button onClick={next} style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", zIndex: 5, background: "rgba(14,207,176,0.18)", border: `1.5px solid rgba(14,207,176,0.4)`, color: "#fff", width: 46, height: 46, borderRadius: "50%", fontSize: 24, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(6px)" }}>›</button>
          <div style={{ position: "absolute", bottom: 22, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 8, zIndex: 5 }}>
            {heroSlides.map((_, i) => (
              <button key={i} onClick={() => setSlideIdx(i)} style={{ width: i === slideIdx ? 28 : 10, height: 10, borderRadius: 5, border: "none", background: i === slideIdx ? T1 : "rgba(255,255,255,0.4)", cursor: "pointer", padding: 0, transition: "width .3s" }} />
            ))}
          </div>
        </div>
      </div>

      {/* OUR COMPREHENSIVE SERVICES BANNER */}
      <section style={{ background: NAVY, padding: "56px 24px", textAlign: "center", position: "relative" }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: TEAL_GRAD }} />
        <p style={{ color: T1, fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 3, margin: "0 0 8px" }}>What We Offer</p>
        <h2 style={{ color: "#fff", fontSize: "clamp(1.4rem,3vw,2rem)", fontWeight: 800, margin: "0 0 12px" }}>Our Comprehensive Services</h2>
        <div style={{ width: 54, height: 3.5, background: TEAL_GRAD, borderRadius: 2, margin: "0 auto 20px" }} />
        <p style={{ color: "rgba(255,255,255,0.65)", fontSize: 14.5, maxWidth: 740, margin: "0 auto 42px", lineHeight: 1.78 }}>
          We provide end-to-end event management solutions to bring your vision to life.
        </p>
        <div style={{ display: "flex", maxWidth: 1200, margin: "0 auto", flexWrap: "wrap" }}>
          {ourServiceCards.map(c => (
            <div key={c.title} className="os-c" style={{ flex: "1 1 260px", position: "relative", overflow: "hidden", cursor: "pointer" }}>
              <img src={c.img} alt={c.title} style={{ width: "100%", height: 300, objectFit: "cover", display: "block", transition: "transform .4s" }} />
              <div className="ov" style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: `linear-gradient(transparent,rgba(7,122,110,0.82))`, padding: "32px 22px 24px" }}>
                <h3 style={{ color: "#fff", fontWeight: 700, fontSize: 17, margin: 0 }}>{c.title}</h3>
                <div style={{ width: 30, height: 2, background: T1, borderRadius: 2, marginTop: 8 }} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES GRID */}
      <section style={{ background: "#fff", padding: "70px 24px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 50 }}>
            <p style={{ color: T2, fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 2.5 }}>End-to-End Solutions</p>
            <h2 style={{ fontSize: "clamp(1.4rem,3vw,2.1rem)", fontWeight: 800, color: DARK }}>Every Detail Covered</h2>
            <div style={{ width: 54, height: 3.5, background: TEAL_GRAD, borderRadius: 2, margin: "16px auto" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 30 }}>
            {serviceGrid.map(svc => (
              <a key={svc.title} href="/services" className="sc" style={{ borderRadius: 20, overflow: "hidden", background: NAVY, textDecoration: "none", display: "block", transition: "transform 0.3s", boxShadow: "0 12px 28px rgba(0,0,0,0.2)", border: `1px solid ${T1}60` }}>
                <div style={{ overflow: "hidden" }}>
                  <img src={svc.img} alt={svc.title} style={{ width: "100%", height: 280, objectFit: "cover", display: "block", transition: "transform 0.5s" }} />
                </div>
                <div style={{ padding: "18px", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, background: "rgba(13,27,42,0.9)", backdropFilter: "blur(2px)" }}>
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: T1 }} />
                  <h3 style={{ fontWeight: 700, fontSize: 16, color: "#fff", margin: 0 }}>{svc.title}</h3>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* TALENT MANAGEMENT SECTION */}
      <section style={{ background: "#f0fbf9", padding: "70px 24px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", textAlign: "center" }}>
          <p style={{ color: T2, fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 2.5 }}>Talent Management</p>
          <h2 style={{ fontSize: "clamp(1.4rem,3vw,2.1rem)", fontWeight: 800, color: DARK }}>India's most trusted talent management ecosystem, turning artists into icons</h2>
          <div style={{ width: 54, height: 3.5, background: TEAL_GRAD, borderRadius: 2, margin: "16px auto 30px" }} />

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 30, marginBottom: 60 }}>
            {talentServices.map(ts => (
              <div key={ts.title} className="ts-card" style={{ position: "relative", borderRadius: 16, overflow: "hidden", boxShadow: "0 8px 20px rgba(0,0,0,0.1)", background: "#fff", transition: "transform 0.3s, box-shadow 0.3s" }}>
                <img src={ts.img} alt={ts.title} style={{ width: "100%", height: 180, objectFit: "cover" }} />
                <div style={{ padding: "20px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                    <div style={{ color: T1 }}>{ts.icon}</div>
                    <h3 style={{ fontSize: 18, fontWeight: 800, color: DARK, margin: 0 }}>{ts.title}</h3>
                    {ts.premium && <span style={{ background: TEAL_GRAD, color: "#fff", fontSize: 10, padding: "2px 8px", borderRadius: 20, fontWeight: 600 }}>Premium</span>}
                  </div>
                  <p style={{ color: "#555", fontSize: 13.5, lineHeight: 1.6, marginBottom: 12 }}>{ts.description}</p>
                  <div style={{ display: "inline-block", background: NAVY, color: "#fff", fontSize: 12, padding: "4px 12px", borderRadius: 20 }}>{ts.stats}</div>
                </div>
              </div>
            ))}
          </div>

          <h3 style={{ fontSize: 22, fontWeight: 800, color: DARK, marginBottom: 24 }}>We Work With Industry's Best</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 20, marginBottom: 40 }}>
            {talentTypes.map(tt => (
              <div key={tt.name} className="tt-card" style={{ background: "#fff", borderRadius: 16, padding: "20px", textAlign: "center", boxShadow: "0 4px 12px rgba(0,0,0,0.08)", transition: "transform 0.3s", cursor: "pointer" }}>
                <div style={{ fontSize: 48, marginBottom: 10 }}>{tt.icon}</div>
                <h4 style={{ fontSize: 16, fontWeight: 700, color: DARK, margin: 0 }}>{tt.name}</h4>
              </div>
            ))}
          </div>

          <div style={{ background: TEAL_GRAD, borderRadius: 16, padding: "40px 30px", color: "#fff", maxWidth: 800, margin: "0 auto" }}>
            <h3 style={{ fontSize: 24, marginBottom: 12 }}>Ready to Elevate Your Career?</h3>
            <p>Join India's most prestigious talent management network. Limited slots available.</p>
            <button onClick={() => setOpenForm(true)} style={{ marginTop: 20, background: "#fff", color: T2, border: "none", padding: "12px 30px", borderRadius: 40, fontWeight: 700, cursor: "pointer" }}>Apply Now</button>
          </div>
        </div>
      </section>

      {/* EVENT CATEGORIES */}
      <section style={{ background: "#fff", padding: "70px 24px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", textAlign: "center" }}>
          <p style={{ color: T2, fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 2.5 }}>We Specialize In</p>
          <h2 style={{ fontSize: "clamp(1.4rem,3vw,2.1rem)", fontWeight: 800, color: DARK }}>Event Categories</h2>
          <div style={{ width: 54, height: 3.5, background: TEAL_GRAD, borderRadius: 2, margin: "16px auto 40px" }} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 30 }}>
            {eventCategories.map(cat => (
              <div key={cat.name} className="ec-card" style={{ position: "relative", borderRadius: 16, overflow: "hidden", boxShadow: "0 8px 20px rgba(0,0,0,0.1)", cursor: "pointer", transition: "transform 0.3s, box-shadow 0.3s" }}>
                <img src={cat.img} alt={cat.name} style={{ width: "100%", height: 200, objectFit: "cover", display: "block" }} />
                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: `linear-gradient(transparent,rgba(7,122,110,0.85))`, padding: "20px", textAlign: "center" }}>
                  <h3 style={{ color: "#fff", fontWeight: 700, fontSize: 16, margin: 0 }}>{cat.name}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonial" style={{ background: "#f0fbf9", padding: "70px 24px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 50 }}>
            <p style={{ color: T2, fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 2.5 }}>What Our Clients Say</p>
            <h2 style={{ fontSize: "clamp(1.4rem,3vw,2.1rem)", fontWeight: 800, color: DARK }}>Testimonials</h2>
            <div style={{ width: 54, height: 3.5, background: TEAL_GRAD, borderRadius: 2, margin: "16px auto" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 24 }}>
            {testimonials.map(t => (
              <div key={t.name} className="tc" style={{ background: "#fff", border: `1.5px solid #e4f8f5`, borderRadius: 12, padding: "28px", boxShadow: "0 4px 16px rgba(14,207,176,0.08)" }}>
                <div style={{ color: T1, fontSize: 16, letterSpacing: 1, marginBottom: 14 }}>{"★".repeat(t.stars)}</div>
                <p style={{ color: "#555", fontSize: 14, lineHeight: 1.78, marginBottom: 20, fontStyle: "italic" }}>"{t.text}"</p>
                <div style={{ borderTop: `1px solid #e4f8f5`, paddingTop: 16 }}>
                  <h4 style={{ fontWeight: 700, fontSize: 14, color: DARK, margin: 0 }}>{t.name}</h4>
                  <p style={{ color: "#aaa", fontSize: 12, marginTop: 4 }}>{t.designation}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer />

      {/* WhatsApp float */}
      <a href="https://wa.me/918100364941" target="_blank" rel="noreferrer" style={{ position: "fixed", bottom: 24, right: 24, width: 58, height: 58, borderRadius: "50%", background: "#25d366", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 20px rgba(37,211,102,0.4)", zIndex: 9998, textDecoration: "none", fontSize: 28 }}>💬</a>

      <ConsultDialog open={openForm} onClose={() => setOpenForm(false)} />
    </div>
  );
}